import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus,
  Edit,
  Trash2,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  Save,
  Send,
  Eye,
  EyeOff,
  Bot,
  FileText,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { settingsCache } from '@/utils/settingsCache';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface HelpItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

interface HelpRequest {
  id: string;
  user_id: string;
  email: string;
  issue: string;
  status: string;
  admin_notes: string | null;
  admin_response: string | null;
  ticket_number: string | null;
  category: string | null;
  created_at: string;
}

interface AiDoc {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  created_at: string;
}

const HelpManagement = () => {
  // --- FAQ state ---
  const [helpItems, setHelpItems] = useState<HelpItem[]>([]);
  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<HelpItem | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<HelpRequest | null>(
    null,
  );
  const [itemFormData, setItemFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
  });
  const [requestNotes, setRequestNotes] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [requestSending, setRequestSending] = useState(false);

  // --- AI & Settings state ---
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [ticketEnabled, setTicketEnabled] = useState(true);
  const [supportUrl, setSupportUrl] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);
  // Store row IDs so we can update by id (required by RLS policy)
  const [aiSettingIds, setAiSettingIds] = useState<Record<string, string>>({});

  // --- AI docs state ---
  const [aiDocs, setAiDocs] = useState<AiDoc[]>([]);
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<AiDoc | null>(null);
  const [docFormData, setDocFormData] = useState({ title: '', content: '' });

  const { toast } = useToast();
  const { session } = useAuth();

  useEffect(() => {
    fetchHelpItems();
    fetchHelpRequests();
    fetchAiSettings();
    fetchAiDocs();
  }, []);

  // ── FAQ ──────────────────────────────────────────────────────────────────

  const fetchHelpItems = async () => {
    try {
      const { data, error } = await supabase
        .from('help_items')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setHelpItems(data || []);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch help items',
        variant: 'destructive',
      });
    }
  };

  const fetchHelpRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('help_requests')
        .select('id, user_id, email, issue, status, admin_notes, admin_response, ticket_number, category, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setHelpRequests(data || []);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch help requests',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!session) throw new Error('Not authenticated');
      const itemData = {
        ...itemFormData,
        created_by: session.user.id,
        is_active: true,
      };
      const result = editingItem
        ? await supabase
            .from('help_items')
            .update(itemData)
            .eq('id', editingItem.id)
            .select()
            .single()
        : await supabase.from('help_items').insert(itemData).select().single();
      if (result.error) throw result.error;
      toast({
        title: 'Success',
        description: `Help item ${editingItem ? 'updated' : 'created'} successfully`,
      });
      fetchHelpItems();
      setIsItemDialogOpen(false);
      resetItemForm();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save help item',
        variant: 'destructive',
      });
    }
  };

  const deleteHelpItem = async (id: string) => {
    try {
      const { error } = await supabase.from('help_items').delete().eq('id', id);
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Help item deleted successfully',
      });
      fetchHelpItems();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete help item',
        variant: 'destructive',
      });
    }
  };

  const resetItemForm = () => {
    setItemFormData({ question: '', answer: '', category: 'general' });
    setEditingItem(null);
  };

  const openEditItemDialog = (item: HelpItem) => {
    setEditingItem(item);
    setItemFormData({
      question: item.question,
      answer: item.answer,
      category: item.category,
    });
    setIsItemDialogOpen(true);
  };

  // ── Help Requests ─────────────────────────────────────────────────────────

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('help_requests')
        .update({ status: newStatus, admin_notes: requestNotes })
        .eq('id', requestId);
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Help request updated successfully',
      });
      fetchHelpRequests();
      setIsRequestDialogOpen(false);
      setSelectedRequest(null);
      setRequestNotes('');
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update help request',
        variant: 'destructive',
      });
    }
  };

  const sendResponse = async () => {
    if (!selectedRequest || !adminResponse.trim()) return;
    setRequestSending(true);
    try {
      const { error } = await supabase
        .from('help_requests')
        .update({
          admin_response: adminResponse.trim(),
          admin_notes: requestNotes,
          status: 'in_progress',
        })
        .eq('id', selectedRequest.id);
      if (error) throw error;

      // Send reply email via edge function
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('https://giedkdxtrqcqzcockacd.supabase.co/functions/v1/send-support-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          type: 'reply',
          to: selectedRequest.email,
          ticketNumber: selectedRequest.ticket_number ?? selectedRequest.id,
          adminResponse: adminResponse.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Email failed to send');
      }

      toast({ title: 'Response sent', description: `Reply emailed to ${selectedRequest.email}` });
      fetchHelpRequests();
      setIsRequestDialogOpen(false);
      setSelectedRequest(null);
      setRequestNotes('');
      setAdminResponse('');
    } catch (err) {
      toast({
        title: 'Error',
        description: String(err instanceof Error ? err.message : err),
        variant: 'destructive',
      });
    } finally {
      setRequestSending(false);
    }
  };

  const openRequestDialog = (request: HelpRequest) => {
    setSelectedRequest(request);
    setRequestNotes(request.admin_notes || '');
    setAdminResponse(request.admin_response || '');
    setIsRequestDialogOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className='h-4 w-4 text-green-600' />;
      case 'in_progress':
        return <Clock className='h-4 w-4 text-yellow-600' />;
      default:
        return <AlertCircle className='h-4 w-4 text-red-600' />;
    }
  };

  // ── AI & Settings ─────────────────────────────────────────────────────────

  const fetchAiSettings = async () => {
    const { data } = await supabase
      .from('system_settings')
      .select('id, setting_key, setting_value')
      .in('setting_key', [
        'anthropic_api_key',
        'help_ai_chat_enabled',
        'ticket_submission_enabled',
        'support_url',
      ]);

    if (data) {
      const map = Object.fromEntries(
        data.map((s) => [s.setting_key, s.setting_value]),
      );
      const ids = Object.fromEntries(data.map((s) => [s.setting_key, s.id]));
      setAiSettingIds(ids);
      setAnthropicApiKey(map['anthropic_api_key'] ?? '');
      setChatEnabled(map['help_ai_chat_enabled'] !== 'false');
      setTicketEnabled(map['ticket_submission_enabled'] !== 'false');
      setSupportUrl(map['support_url'] ?? '');
    }
  };

  const saveAiSettings = async () => {
    setSettingsSaving(true);
    try {
      const updates = [
        { key: 'anthropic_api_key', value: anthropicApiKey },
        { key: 'help_ai_chat_enabled', value: chatEnabled ? 'true' : 'false' },
        { key: 'ticket_submission_enabled', value: ticketEnabled ? 'true' : 'false' },
        { key: 'support_url', value: supportUrl },
      ];

      for (const { key, value } of updates) {
        const id = aiSettingIds[key];
        if (!id) throw new Error(`Row id not found for "${key}". Try refreshing the page.`);
        const { error } = await supabase
          .from('system_settings')
          .update({ setting_value: value })
          .eq('id', id);
        if (error) throw new Error(`Failed to update ${key}: ${error.message}`);
      }

      settingsCache.clearKey('help_ai_chat_enabled');
      settingsCache.clearKey('ticket_submission_enabled');
      settingsCache.clearKey('support_url');
      setSettingsDirty(false);

      toast({ title: 'Settings saved', description: 'Help Center settings updated successfully.' });
    } catch (err) {
      toast({ title: 'Error', description: String(err instanceof Error ? err.message : err), variant: 'destructive' });
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── AI Docs ───────────────────────────────────────────────────────────────

  const fetchAiDocs = async () => {
    const { data } = await supabase
      .from('help_ai_docs')
      .select('id, title, content, is_active, created_at')
      .order('created_at', { ascending: true });
    setAiDocs(data ?? []);
  };

  const handleDocSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = editingDoc
        ? await supabase
            .from('help_ai_docs')
            .update(docFormData)
            .eq('id', editingDoc.id)
            .select()
            .single()
        : await supabase
            .from('help_ai_docs')
            .insert({ ...docFormData, is_active: true })
            .select()
            .single();
      if (result.error) throw result.error;
      toast({
        title: 'Success',
        description: `AI doc ${editingDoc ? 'updated' : 'created'} successfully`,
      });
      fetchAiDocs();
      setIsDocDialogOpen(false);
      resetDocForm();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save AI doc',
        variant: 'destructive',
      });
    }
  };

  const toggleDocActive = async (doc: AiDoc) => {
    const { error } = await supabase
      .from('help_ai_docs')
      .update({ is_active: !doc.is_active })
      .eq('id', doc.id);
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to update doc status',
        variant: 'destructive',
      });
    } else {
      fetchAiDocs();
    }
  };

  const deleteAiDoc = async (id: string) => {
    const { error } = await supabase.from('help_ai_docs').delete().eq('id', id);
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete AI doc',
        variant: 'destructive',
      });
    } else {
      fetchAiDocs();
    }
  };

  const resetDocForm = () => {
    setDocFormData({ title: '', content: '' });
    setEditingDoc(null);
  };

  const openEditDocDialog = (doc: AiDoc) => {
    setEditingDoc(doc);
    setDocFormData({ title: doc.title, content: doc.content });
    setIsDocDialogOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Card>
        <CardContent className='flex items-center justify-center py-8'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs defaultValue='faq' className='space-y-6'>
        <TabsList>
          <TabsTrigger value='faq'>FAQ Management</TabsTrigger>
          <TabsTrigger value='requests'>
            Help Requests
            {helpRequests.filter((r) => r.status === 'open').length > 0 && (
              <Badge variant='destructive' className='ml-2 text-xs px-1.5 py-0'>
                {helpRequests.filter((r) => r.status === 'open').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='ai-settings'>AI &amp; Settings</TabsTrigger>
        </TabsList>

        {/* ── FAQ Tab ── */}
        <TabsContent value='faq'>
          <Card>
            <CardHeader>
              <div className='flex justify-between items-center'>
                <div>
                  <CardTitle>FAQ Management</CardTitle>
                  <CardDescription>
                    Manage frequently asked questions shown on the Help page
                  </CardDescription>
                </div>
                <Dialog
                  open={isItemDialogOpen}
                  onOpenChange={setIsItemDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button onClick={resetItemForm}>
                      <Plus className='h-4 w-4 mr-2' />
                      Add FAQ
                    </Button>
                  </DialogTrigger>
                  <DialogContent className='max-w-2xl'>
                    <DialogHeader>
                      <DialogTitle>
                        {editingItem ? 'Edit FAQ Item' : 'Add FAQ Item'}
                      </DialogTitle>
                      <DialogDescription>
                        Create or update a frequently asked question
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleItemSubmit} className='space-y-4'>
                      <div>
                        <label className='text-sm font-medium'>Category</label>
                        <Select
                          value={itemFormData.category}
                          onValueChange={(value) =>
                            setItemFormData({
                              ...itemFormData,
                              category: value,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='general'>General</SelectItem>
                            <SelectItem value='campaigns'>Campaigns</SelectItem>
                            <SelectItem value='billing'>Billing</SelectItem>
                            <SelectItem value='technical'>Technical</SelectItem>
                            <SelectItem value='compliance'>
                              Compliance
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className='text-sm font-medium'>Question</label>
                        <Input
                          value={itemFormData.question}
                          onChange={(e) =>
                            setItemFormData({
                              ...itemFormData,
                              question: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div>
                        <label className='text-sm font-medium'>Answer</label>
                        <Textarea
                          value={itemFormData.answer}
                          onChange={(e) =>
                            setItemFormData({
                              ...itemFormData,
                              answer: e.target.value,
                            })
                          }
                          rows={5}
                          required
                        />
                      </div>
                      <div className='flex justify-end gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => setIsItemDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button type='submit'>
                          {editingItem ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {helpItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className='font-medium max-w-xs truncate'>
                          {item.question}
                        </TableCell>
                        <TableCell>
                          <Badge variant='outline'>{item.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={item.is_active ? 'default' : 'destructive'}
                          >
                            {item.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(item.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className='flex gap-2'>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openEditItemDialog(item)}
                            >
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              size='sm'
                              variant='destructive'
                              onClick={() => deleteHelpItem(item.id)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {helpItems.length === 0 && (
                <div className='text-center py-8 text-muted-foreground'>
                  No FAQ items found. Create your first FAQ to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Help Requests Tab ── */}
        <TabsContent value='requests'>
          <Card>
            <CardHeader>
              <CardTitle>Help Requests</CardTitle>
              <CardDescription>
                Support tickets submitted by users from the Help page
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Issue</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {helpRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className='font-mono text-xs text-muted-foreground whitespace-nowrap'>
                          {request.ticket_number ?? '—'}
                        </TableCell>
                        <TableCell className='font-medium'>
                          {request.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant='outline' className='capitalize text-xs'>
                            {request.category ?? 'general'}
                          </Badge>
                        </TableCell>
                        <TableCell className='max-w-xs truncate'>
                          {request.issue}
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            {getStatusIcon(request.status)}
                            <Badge
                              variant={
                                request.status === 'resolved'
                                  ? 'default'
                                  : request.status === 'in_progress'
                                    ? 'secondary'
                                    : 'destructive'
                              }
                            >
                              {request.status.replace('_', ' ')}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(request.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => openRequestDialog(request)}
                          >
                            <MessageSquare className='h-4 w-4 mr-2' />
                            Respond
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {helpRequests.length === 0 && (
                <div className='text-center py-8 text-muted-foreground'>
                  No help requests found.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── AI & Settings Tab ── */}
        <TabsContent value='ai-settings' className='space-y-6'>
          {/* Chat settings card */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Bot className='h-5 w-5' />
                AI Chat Assistant
                {settingsDirty && (
                  <Badge variant='outline' className='ml-2 text-xs text-amber-600 border-amber-400 bg-amber-50 dark:bg-amber-950 dark:text-amber-400'>
                    Unsaved changes
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Configure the AI chat widget shown on the Help page. Powered by
                Claude Haiku — estimated cost $6–60/month depending on usage.
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-5'>
              {/* Enable toggle */}
              <div className='flex items-center justify-between rounded-lg border p-4'>
                <div>
                  <p className='text-sm font-medium'>Enable AI Chat Widget</p>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    Hide this to stop AI API usage immediately if costs become a
                    concern.
                  </p>
                </div>
                <Switch
                  checked={chatEnabled}
                  onCheckedChange={(v) => { setChatEnabled(v); setSettingsDirty(true); }}
                />
              </div>

              {/* Ticket submission toggle */}
              <div className='flex items-center justify-between rounded-lg border p-4'>
                <div>
                  <p className='text-sm font-medium'>Enable In-App Ticket Submission</p>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    Show the ticket submission form on the Help page. Disable if you prefer users to use an external help desk only.
                  </p>
                </div>
                <Switch
                  checked={ticketEnabled}
                  onCheckedChange={(v) => { setTicketEnabled(v); setSettingsDirty(true); }}
                />
              </div>

              {/* API key */}
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>Anthropic API Key</label>
                <div className='relative'>
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={anthropicApiKey}
                    onChange={(e) => { setAnthropicApiKey(e.target.value); setSettingsDirty(true); }}
                    placeholder='sk-ant-...'
                    className='pr-10'
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='absolute right-0 top-0 h-full px-3'
                    onClick={() => setShowApiKey((v) => !v)}
                  >
                    {showApiKey ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </Button>
                </div>
                <p className='text-xs text-muted-foreground'>
                  Get your key at console.anthropic.com. The chat widget will
                  show an error to users if this is blank.
                </p>
              </div>

              {/* Support URL */}
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>
                  Support Ticket URL
                </label>
                <Input
                  type='url'
                  value={supportUrl}
                  onChange={(e) => { setSupportUrl(e.target.value); setSettingsDirty(true); }}
                  placeholder='https://your-helpdesk.com/ticket/'
                />
                <p className='text-xs text-muted-foreground'>
                  Shown as the "Still need help?" CTA on the Help page. Leave
                  blank to hide the button.
                </p>
              </div>

              <Button
                onClick={saveAiSettings}
                disabled={settingsSaving}
                variant={settingsDirty ? 'default' : 'outline'}
              >
                <Save className='h-4 w-4 mr-2' />
                {settingsSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>

          {/* AI Training Docs card */}
          <Card>
            <CardHeader>
              <div className='flex justify-between items-center'>
                <div>
                  <CardTitle className='flex items-center gap-2'>
                    <FileText className='h-5 w-5' />
                    AI Training Documents
                  </CardTitle>
                  <CardDescription>
                    Knowledge base entries included in the AI chat's context.
                    Add video transcripts, how-to guides, or any content you
                    want the assistant to know about.
                  </CardDescription>
                </div>
                <Dialog
                  open={isDocDialogOpen}
                  onOpenChange={setIsDocDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button onClick={resetDocForm}>
                      <Plus className='h-4 w-4 mr-2' />
                      Add Document
                    </Button>
                  </DialogTrigger>
                  <DialogContent className='max-w-3xl'>
                    <DialogHeader>
                      <DialogTitle>
                        {editingDoc ? 'Edit AI Document' : 'Add AI Document'}
                      </DialogTitle>
                      <DialogDescription>
                        Paste a transcript or write a knowledge base entry. The
                        AI will use this when answering user questions.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleDocSubmit} className='space-y-4'>
                      <div className='space-y-1.5'>
                        <label className='text-sm font-medium'>Title</label>
                        <Input
                          value={docFormData.title}
                          onChange={(e) =>
                            setDocFormData({
                              ...docFormData,
                              title: e.target.value,
                            })
                          }
                          placeholder='e.g. Campaign Management, Getting Started'
                          required
                        />
                        <p className='text-xs text-muted-foreground'>
                          Use a topic name that matches the video titles so the
                          AI can route questions correctly.
                        </p>
                      </div>
                      <div className='space-y-1.5'>
                        <label className='text-sm font-medium'>Content</label>
                        <Textarea
                          value={docFormData.content}
                          onChange={(e) =>
                            setDocFormData({
                              ...docFormData,
                              content: e.target.value,
                            })
                          }
                          rows={14}
                          placeholder='Paste transcript or knowledge base content here...'
                          required
                          className='font-mono text-xs'
                        />
                      </div>
                      <div className='flex justify-end gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => setIsDocDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button type='submit'>
                          {editingDoc ? 'Update' : 'Add Document'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Characters</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className='font-medium'>
                          {doc.title}
                        </TableCell>
                        <TableCell className='text-muted-foreground text-sm'>
                          {doc.content.length.toLocaleString()} chars
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={doc.is_active ? 'default' : 'secondary'}
                          >
                            {doc.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(doc.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className='flex gap-2'>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openEditDocDialog(doc)}
                            >
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => toggleDocActive(doc)}
                            >
                              {doc.is_active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              size='sm'
                              variant='destructive'
                              onClick={() => deleteAiDoc(doc.id)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {aiDocs.length === 0 && (
                <div className='text-center py-8 text-muted-foreground'>
                  No AI documents yet. Add your first transcript or knowledge
                  base entry to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Help Request Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              Manage Help Request
              {selectedRequest?.ticket_number && (
                <Badge variant='outline' className='font-mono text-xs'>
                  {selectedRequest.ticket_number}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Respond to the user via email or update the internal status and notes
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <p className='font-medium text-xs text-muted-foreground uppercase tracking-wide mb-0.5'>User Email</p>
                  <p>{selectedRequest.email}</p>
                </div>
                <div>
                  <p className='font-medium text-xs text-muted-foreground uppercase tracking-wide mb-0.5'>Category</p>
                  <Badge variant='outline' className='capitalize'>{selectedRequest.category ?? 'general'}</Badge>
                </div>
              </div>
              <div>
                <p className='font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1'>Issue Description</p>
                <p className='text-sm bg-muted p-3 rounded whitespace-pre-wrap'>
                  {selectedRequest.issue}
                </p>
              </div>

              {/* Admin Response (emailed to user) */}
              <div>
                <label className='text-sm font-medium'>Response to User <span className='text-xs text-muted-foreground font-normal'>(sent via email)</span></label>
                <Textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Write your reply here. This will be emailed to the user when you click 'Send Response'."
                  rows={5}
                  className='mt-1.5'
                />
              </div>

              {/* Internal Notes */}
              <div>
                <label className='text-sm font-medium'>Internal Notes <span className='text-xs text-muted-foreground font-normal'>(not sent to user)</span></label>
                <Textarea
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  placeholder='Private notes visible only to admins...'
                  rows={2}
                  className='mt-1.5'
                />
              </div>

              <div className='flex justify-between gap-2 pt-1'>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    onClick={() =>
                      updateRequestStatus(selectedRequest.id, 'in_progress')
                    }
                  >
                    Mark In Progress
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() =>
                      updateRequestStatus(selectedRequest.id, 'resolved')
                    }
                  >
                    Mark Resolved
                  </Button>
                </div>
                <Button
                  onClick={sendResponse}
                  disabled={!adminResponse.trim() || requestSending}
                >
                  {requestSending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4 mr-2' />
                      Send Response
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpManagement;
